host/<template>
  <Sidebar
    :visible="visible"
    @update:visible="onToggle"
    class="w-full sm:w-40rem"
    position="right"
    header="Invite User"
    :pt="{
      content: {
        class: 'flex flex-column'
      }
    }"
    blockScroll
    :dismissable="false"
  >
    <div class="flex flex-column flex-grow-1 overflow-y-auto overflow-x-hidden">
      <VeeForm
        v-slot="{ handleSubmit }"
        :validation-schema="schema"
        as="div"
        @invalidSubmit="onInvalidSubmit"
      >
        <p>
          Invite a host user by email. Once the podcast is setup within Ekoz,
          the host user can log in and setup their own voice profile.
        </p>
        <WarningMessage
          class="mb-3"
          severity="secondary"
        >
          <!-- eslint-disable max-len -->
          A confirmation email will be sent to the address you provide. In the email, there will be a link where the user can navigate to set up their password. Please check the inbox (and spam folder, just in case) to find the invitation email.
          <!-- eslint-enable max-len -->
        </WarningMessage>
        <form
          id="invite-user-form"
          @submit.prevent="handleSubmit($event, onSubmit)"
        >
        <div class="grid">
          <div class="col-6">
            <BaseInput
              v-model="form.firstName"
              fieldId="firstName"
              fieldName="firstName"
              fieldLabel="First Name"
            />
          </div>
          <div class="col-6">
            <BaseInput
              v-model="form.lastName"
              fieldId="lastName"
              fieldName="lastName"
              fieldLabel="Last Name"
            />
          </div>
        </div>
          <BaseInput
            v-model="form.email"
            fieldId="email"
            fieldName="email"
            fieldLabel="Email"
            type="email"
          />
        </form>
      </VeeForm>
    </div>

    <div class="flex justify-content-between pt-2">
      <Button
        label="Cancel"
        text
        plain
        :disabled="isSubmitting"
        @click="() => onToggle(false)"
      />
      <Button
        label="Submit"
        type="submit"
        form="invite-user-form"
        :loading="isSubmitting"
      />
    </div>
  </Sidebar>
</template>

<script>
import { mapStores } from 'pinia';
import { object, string } from 'yup';
import { INVALID_FORM_SUBMISSION_MESSAGE } from '@/utils/messages';
import { parseMessageFromError } from '@/utils/errors';
import * as api from '@/api';
import {
  useMyUserStore,
  useUsersStore,
  useRolesStore,
} from '@/stores';

export default {
  props: {
    visible: Boolean,
  },
  computed: {
    ...mapStores(
      useMyUserStore,
      useUsersStore,
      useRolesStore,
    ),
  },
  data() {
    return {
      isSubmitting: false,
      schema: object({
        firstName: string().required('First name is required'),
        lastName: string().required('Last name is required'),
        email: string().email().required('Email is required'),
      }),
      form: {
        firstName: '',
        lastName: '',
        email: '',
      },
    };
  },
  methods: {
    onInvalidSubmit() {
      this.$toast.add({
        severity: 'warn',
        detail: INVALID_FORM_SUBMISSION_MESSAGE,
      });
    },
    onToggle(visible) {
      if (this.isSubmitting) return;

      this.$emit('update:visible', visible);
    },
    async onSubmit(form, { resetForm }) {
      try {
        this.isSubmitting = true;

        const roleId = this.rolesStore.hostUserRole.id;

        const invitedUser = await api.inviteUser({
          organizationId: this.myUserStore.myOrganization.id,
          email: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
        });
        await api.updateUser({
          userId: invitedUser.user_id,
          roleId,
        });

        this.$toast.add({
          severity: 'success',
          detail: 'Successfully invited host user.',
        });

        await this.usersStore.getUsers({
          organizationId: this.myUserStore.myOrganization.id,
        });

        this.$emit('selectUserId', invitedUser.user_id);
        this.$emit('update:visible', false);

        resetForm();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error inviting user.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
